import React, { useEffect, useRef, useState } from 'react';
import { CalendarDays, ChevronDown, ChevronUp, Search } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styles from "../../_css/ReportingAndSterring.module.css";
import { useDashboardFilters } from './context/DashboardContext.js';
import { useClickOutsideWithCallback } from "../hooks/useClickOutside.js"
import { getCompaniesList } from "../../requests.js"

const DashboardHeader = () => {
    const { filters, updateFilters } = useDashboardFilters();
    const [showDropdown, setShowDropdown] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [companyOptions, setCompanyOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const toggleDropdown = () => setShowDropdown(!showDropdown);
    const dropdownRef = useRef(null);

    useClickOutsideWithCallback(dropdownRef, () => {
        setShowDropdown(false)
    })

    // Fetch companies on component mount
    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                setLoading(true);
                setError(null);
                const companies = await getCompaniesList();
                setCompanyOptions(companies.result);
            } catch (err) {
                setError('Failed to load companies');
                console.error('Error fetching companies:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchCompanies();
    }, []);

    const selectCompany = (company) => {
        if (company === 'all') {
            updateFilters({ company: 'all' });
        } else {
            updateFilters({ company: company.name });
        }
        setShowDropdown(false);
        setSearchTerm(''); // Clear search when selecting
    };

    const handleFromDateChange = (date) => {
        updateFilters({
            dateRange: {
                ...filters.dateRange,
                from: date
            }
        });
    };

    const handleToDateChange = (date) => {
        updateFilters({
            dateRange: {
                ...filters.dateRange,
                to: date
            }
        });
    };

    // Filter company options based on search term
    const filteredCompanies = companyOptions?.filter(company =>
        company?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Get display name for selected company
    const getSelectedCompanyDisplayName = () => {
        if (filters.company === 'all') return 'All';
        if (filters.company && typeof filters.company === 'object') {
            return filters.company.name;
        }
        // Handle case where company might be a string (company name)
        return filters.company || 'All';
    };

    return (
        <div className={styles.dashboardHeader}>
            {/* Company Filter Dropdown */}
            <div className={styles.dropdownContainer} ref={dropdownRef}>
                <button
                    onClick={toggleDropdown}
                    className={styles.dropdownButton}
                    disabled={loading}
                >
                    {loading ? 'Loading...' : getSelectedCompanyDisplayName()}
                    {showDropdown ? <ChevronUp size={16} color="#6b7280" /> : <ChevronDown size={16} color="#6b7280" />}
                </button>
                {showDropdown && (
                    <div className={styles.dropdownMenu}>
                        <div>
                            <div className={styles.searchContainer}>
                                <input
                                    type="text"
                                    placeholder="Search companies..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    className={styles.searchInput}
                                    disabled={loading || error}
                                />
                                <Search
                                    size={16}
                                    color="#6B7280"
                                    className={styles.searchIcon}
                                />
                            </div>
                            <div>
                                {error && (
                                    <div className={styles.errorMessage}>
                                        {error}
                                    </div>
                                )}
                                {loading ? (
                                    <div className={styles.loadingMessage}>
                                        Loading companies...
                                    </div>
                                ) : (
                                    <>
                                        <div
                                            onClick={() => selectCompany('all')}
                                            className={`${styles.dropdownItem} ${filters.company === 'all' ? styles.dropdownItemActive : ''}`}
                                        >
                                            All
                                        </div>
                                        {filteredCompanies.map(company => (
                                            <div
                                                key={company.name}
                                                onClick={() => selectCompany(company)}
                                                className={`${styles.dropdownItem} ${filters.company === company.name ? styles.dropdownItemActive : ''}`}
                                            >
                                                {company.name}
                                            </div>
                                        ))}
                                        {filteredCompanies.length === 0 && searchTerm && (
                                            <div className={styles.noResults}>
                                                No companies found for "{searchTerm}"
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* From Date Picker */}
            <div className={styles.datePickerContainer}>
                <CalendarDays size={16} color="#6B7280" />
                <DatePicker
                    selected={filters.dateRange.from}
                    onChange={handleFromDateChange}
                    placeholderText="Start Date"
                    popperPlacement="bottom-start"
                    className={styles.customDatepicker}
                />
            </div>

            {/* To Date Picker */}
            <div className={styles.datePickerContainer}>
                <CalendarDays size={16} color='#6B7280' />
                <DatePicker
                    selected={filters.dateRange.to}
                    onChange={handleToDateChange}
                    placeholderText="End Date"
                    popperPlacement="bottom-start"
                    className={styles.customDatepicker}
                />
            </div>
        </div>
    );
};

export default DashboardHeader;